import { Components, switchClasses, Theme, touchRippleClasses } from '@mui/material';
import { green, grey, white } from '../../theme/palette/colors';
import { GetThemeOptions } from '../../theme/theme';

/** Material Icons 24×24 paths (same shapes as mdi Check / Close). */
const SWITCH_THUMB_ICON_CHECK_PATH = 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z';
const SWITCH_THUMB_ICON_CLOSE_PATH =
  'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z';

function switchThumbIconDataUrl(pathD: string, fill: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${fill}" d="${pathD}"/></svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

/**
 * Component overrides (i.e. global styles, default props) applied to
 * MuiSwitch to make it match the design system.
 *
 * See: https://mui.com/material-ui/api/switch/#css
 */
export const getMuiSwitch = (opts: GetThemeOptions): Components<Theme>['MuiSwitch'] => ({
  defaultProps: {
    color: 'primary',
    // Ripple animates under the thumb during toggle and reads like a shadow/glow while the knob slides.
    ...(opts.panwRebrandStubYear && { disableRipple: true, disableFocusRipple: true }),
  },
  // MUI doesn't provide the needed level of granularity via content slots, so overriding
  // with CSS classes instead
  styleOverrides: {
    root: ({ theme }) => {
      const commonStyles = { padding: 9 };
      if (!theme.featureFlags.panwRebrandStubYear) {
        return commonStyles;
      }
      const control = theme.palette.interactive.control;
      return {
        ...commonStyles,
        [`&:not(:has(.${switchClasses.disabled}))`]: {
          [`&:hover:not(:has(.${switchClasses.checked})) .${switchClasses.track}`]: {
            backgroundColor: control.hover,
          },
          [`&:hover:has(.${switchClasses.checked}) .${switchClasses.track}`]: {
            backgroundColor: control.selectedHover,
          },
        },
      };
    },
    switchBase: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        const control = theme.palette.interactive.control;
        return {
          margin: 1,
          // MUI adds a circular hover wash on the thumb (reads like a soft shadow)
          '&:hover': { backgroundColor: 'transparent' },
          [`&.${switchClasses.checked}:hover`]: { backgroundColor: 'transparent' },
          [`&:not(.${switchClasses.checked}):not(.${switchClasses.disabled}) > .${switchClasses.thumb}`]: {
            backgroundColor: control.handle,
            backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CLOSE_PATH, theme.palette.text.primary),
          },
          [`&.${switchClasses.checked}:not(.${switchClasses.disabled}) > .${switchClasses.thumb}`]: {
            backgroundColor: control.handle,
            backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CHECK_PATH, theme.palette.text.primary),
          },
          [`&.${switchClasses.disabled}:not(.${switchClasses.checked}) > .${switchClasses.thumb}`]: {
            backgroundColor: control.handleDisabled,
            backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CLOSE_PATH, theme.palette.text.disabled),
          },
          [`&.${switchClasses.disabled}.${switchClasses.checked} > .${switchClasses.thumb}`]: {
            backgroundColor: control.handleDisabled,
            backgroundImage: switchThumbIconDataUrl(SWITCH_THUMB_ICON_CHECK_PATH, theme.palette.text.disabled),
          },
          [`& .${touchRippleClasses.root}`]: { display: 'none' },
          // bringing in the default browser focus ring, rather than using the MUI focus ring
          [`&.Mui-focusVisible .${switchClasses.thumb}`]: {
            outline: '2px auto -webkit-focus-ring-color',
            outlineOffset: 2,
          },
          [`&.${switchClasses.checked}`]: {
            [`& + .${switchClasses.track}`]: {
              backgroundColor: control.selected,
              opacity: 1,
            },
          },
          [`&.${switchClasses.disabled}`]: {
            [`& + .${switchClasses.track}`]: {
              opacity: 1,
              backgroundColor: control.disabled,
            },
          },
        };
      }

      const thumbDisabledColor = theme.palette.mode === 'light' ? grey[100] : grey[700];
      return {
        margin: 1,
        [`&.${switchClasses.checked}`]: {
          [`& + .${switchClasses.track}`]: {
            backgroundColor: green[600],
            opacity: 1,
          },
        },
        [`&.${switchClasses.disabled}`]: {
          [`& > .${switchClasses.thumb}`]: {
            backgroundColor: thumbDisabledColor,
          },
          [`& + .${switchClasses.track}`]: {
            opacity: 0.3,
          },
        },
      };
    },
    thumb: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          height: 16,
          width: 16,
          boxShadow: 'none',
          margin: '1px 0 0 1px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: '12px 12px',
        };
      }
      return {
        height: 18,
        width: 18,
        backgroundColor: white,
      };
    },
    track: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: theme.palette.interactive.control.default,
          border: `1px solid ${theme.palette.border.default}`,
          borderRadius: 12,
          opacity: 1,
        };
      }
      const trackUncheckedColor = theme.palette.mode === 'light' ? grey[400] : grey[600];
      return {
        backgroundColor: trackUncheckedColor,
        borderRadius: 12,
        opacity: 1,
      };
    },
  },
});
